import React from "react";
import { TREE_NODES, TREE_EDGES } from "@/lib/morse";

/**
 * Morse dichotomic tree as SVG.
 * - Lit nodes are in activePath (array of node ids). Others are grey.
 * - showLetters: toggle letter text inside each shape.
 * - lastElement / calibratingFor / calDot / calDash drive the . and -
 *   indicators above E and T (live light-up + per-element calibration).
 */
const NODE_SIZE = 58; // width/height for square; diameter for circle
const IND_Y = -54;

export default function MorseTree({
  activePath = [],
  showLetters = true,
  className = "",
  onNodeClick,
  disabled = false,
  currentMorse = "",
  calibratingFor = null,
  calDot = null,
  calDash = null,
}) {
  const activeSet = new Set(activePath);
  const handleNodeClick = (n) => {
    if (disabled) return;
    if (!onNodeClick) return;
    if (n.level === 0) return;
    if (!n.letter) return;
    onNodeClick(n);
  };

  // Top dot/dash indicators reflect the LAST symbol added to the current
  // morse-in-progress, not whether E or T is in the path. Stays lit only
  // while the letter is still being built (any tree node still active).
  // Once the letter is finalized (currentMorse cleared), both go dark.
  // Calibration mode forces the corresponding indicator on regardless.
  const lastSym = currentMorse.length > 0
    ? currentMorse[currentMorse.length - 1]
    : null;
  const dashLit = lastSym === "-" || calibratingFor === "dash";
  const dotLit = lastSym === "." || calibratingFor === "dot";
  const dashColor = dashLit ? "var(--led-on)" : "var(--led-off)";
  const dotColor = dotLit ? "var(--led-on)" : "var(--led-off)";
  const dashTextColor = dashLit ? "var(--led-on-text)" : "var(--led-off-text)";
  const dotTextColor = dotLit ? "var(--led-on-text)" : "var(--led-off-text)";

  return (
    <svg
      viewBox="-16 -100 1080 545"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMinYMid meet"
      className={`${className} ${disabled ? "is-busy" : ""}`}
      role="img"
      aria-label="Morse code dichotomic tree"
      data-testid="morse-tree-svg"
    >
      {/* . / - indicators above E (.) and T (-). Click sends a single
          dot or dash through the same auto-key path the tree uses. */}
      <g>
        {/* Dash square above T (which is at x=250) */}
        <g
          transform={`translate(${250 - NODE_SIZE / 2} ${IND_Y - NODE_SIZE / 2})`}
          className={`tree-node clickable cal-indicator ${calibratingFor === "dash" ? "is-calibrating" : ""}`}
          onClick={() => !disabled && onNodeClick && onNodeClick({ id: "ind-dash", morse: "-" })}
          filter={dashLit ? "url(#ledGlow)" : undefined}
          data-testid="cal-dash"
        >
          <rect width={NODE_SIZE} height={NODE_SIZE} fill={dashColor} rx={3} />
          <text
            x={NODE_SIZE / 2}
            y={NODE_SIZE / 2 + 11}
            textAnchor="middle"
            fontSize="40"
            fontFamily="'Azeret Mono', ui-monospace, monospace"
            fontWeight="800"
            fill={dashTextColor}
            pointerEvents="none"
          >
            –
          </text>
        </g>
        {calDash !== null && (
          <text
            x={250}
            y={IND_Y + NODE_SIZE / 2 + 18}
            textAnchor="middle"
            fontSize="13"
            fontFamily="'Azeret Mono', ui-monospace, monospace"
            fontWeight="600"
            fill="var(--led-on)"
            pointerEvents="none"
          >
            {calDash} ms
          </text>
        )}

        {/* Dot circle above E (which is at x=750) */}
        <g
          transform={`translate(750 ${IND_Y})`}
          className={`tree-node clickable cal-indicator ${calibratingFor === "dot" ? "is-calibrating" : ""}`}
          onClick={() => !disabled && onNodeClick && onNodeClick({ id: "ind-dot", morse: "." })}
          filter={dotLit ? "url(#ledGlow)" : undefined}
          data-testid="cal-dot"
        >
          <circle r={NODE_SIZE / 2} fill={dotColor} />
          <circle r={7} fill={dotTextColor} pointerEvents="none" />
        </g>
        {calDot !== null && (
          <text
            x={750}
            y={IND_Y + NODE_SIZE / 2 + 18}
            textAnchor="middle"
            fontSize="13"
            fontFamily="'Azeret Mono', ui-monospace, monospace"
            fontWeight="600"
            fill="var(--led-on)"
            pointerEvents="none"
          >
            {calDot} ms
          </text>
        )}
      </g>

      {/* Tree edges */}
      <g stroke="currentColor" strokeWidth="1.2" opacity="0.45">
        {TREE_EDGES.map(({ from, to }) => (
          <line
            key={`${from.id}-${to.id}`}
            x1={from.x}
            y1={from.y}
            x2={to.x}
            y2={to.y}
          />
        ))}
      </g>

      {/* Tree nodes */}
      <g>
        {TREE_NODES.map((n) => {
          const isActive = activeSet.has(n.id);
          const color = isActive ? "var(--led-on)" : "var(--led-off)";
          const textColor = isActive
            ? "var(--led-on-text)"
            : "var(--led-off-text)";
          const filter = isActive ? "url(#ledGlow)" : undefined;
          const label =
            n.level === 0 ? "" : showLetters ? n.letter : "";

          if (n.shape === "star") {
            return (
              <g key={n.id} transform={`translate(${n.x} ${n.y})`} filter={filter}>
                <path d={starPath(18)} fill={color} data-testid={`node-${n.id}`} />
              </g>
            );
          }
          if (n.shape === "square") {
            const isClickable = n.letter && onNodeClick;
            return (
              <g
                key={n.id}
                transform={`translate(${n.x - NODE_SIZE / 2} ${n.y - NODE_SIZE / 2})`}
                filter={filter}
                onClick={() => handleNodeClick(n)}
                className={isClickable ? "tree-node clickable" : "tree-node"}
              >
                <rect
                  width={NODE_SIZE}
                  height={NODE_SIZE}
                  fill={color}
                  rx={3}
                  data-testid={`node-${n.id}`}
                />
                {label && (
                  <text
                    x={NODE_SIZE / 2}
                    y={NODE_SIZE / 2 + 9}
                    textAnchor="middle"
                    fontSize="26"
                    fontFamily="'Azeret Mono', ui-monospace, monospace"
                    fontWeight="700"
                    fill={textColor}
                    pointerEvents="none"
                  >
                    {label}
                  </text>
                )}
              </g>
            );
          }
          // circle
          const isClickable = n.letter && onNodeClick;
          return (
            <g
              key={n.id}
              transform={`translate(${n.x} ${n.y})`}
              filter={filter}
              onClick={() => handleNodeClick(n)}
              className={isClickable ? "tree-node clickable" : "tree-node"}
            >
              <circle r={NODE_SIZE / 2} fill={color} data-testid={`node-${n.id}`} />
              {label && (
                <text
                  y={9}
                  textAnchor="middle"
                  fontSize="26"
                  fontFamily="'Azeret Mono', ui-monospace, monospace"
                  fontWeight="700"
                  fill={textColor}
                  pointerEvents="none"
                >
                  {label}
                </text>
              )}
            </g>
          );
        })}
      </g>

      {/* Glow filter */}
      <defs>
        <filter id="ledGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
    </svg>
  );
}

function starPath(r) {
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const angle = (Math.PI / 5) * i - Math.PI / 2;
    const rad = i % 2 === 0 ? r : r * 0.45;
    pts.push([Math.cos(angle) * rad, Math.sin(angle) * rad]);
  }
  return "M " + pts.map((p) => p.join(",")).join(" L ") + " Z";
}

